import React from 'react';
import './HeroLanding.css';
import { useNavigate } from 'react-router-dom';

function HeroLanding({
  logoSrc = '/images/icons/nav-logo.png',
  logoAlt = 'The Toronto Clinic',
  phone = '416-849-5555',
  backgroundImage = '/images/creatives/landing-page-2-hero-bg.jpg',
  teamImage = '/images/creatives/team-card-img-1.png',
  tagline = 'YORKVILLE • HOSPITAL & MEDICAL CARE',
  headingLine1 = 'Raising',
  headingLine2 = 'The Bar On',
  headingItalic = 'Personalized',
  headingLine3 = 'Care.',
  subtext = 'Physician-led membership for people who would rather prevent disease than negotiate with it. Experienced doctors, a modern clinic, trusted care — closer to home.',
  getStartedHref = '/book-a-consultation/membership',
  teamLedSectionId = 'Team-led-care',
  membershipSectionId = 'Membership-section'
}) {
  // Smooth scroll helper for section references
  const scrollToSection = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigate = useNavigate()

  return (
    <section className="ttc-hero-section position-relative min-vh-100 d-flex flex-column justify-content-between">
      {/* Background Image Layer */}
      <div 
        className="ttc-hero-bg" 
        style={{ backgroundImage: `url('${backgroundImage}')` }}
      />
      {/* Dark Gradient Overlay */}
      <div className="ttc-hero-overlay" />

      {/* TOP HEADER BAR */}
      <header className="ttc-hero-nav position-relative pt-4 pb-2">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between">
            {/* Left Logo */}
            <a href="/" className="ttc-hero-logo-link">
              <img 
                src={logoSrc} 
                alt={logoAlt} 
                className="img-fluid ttc-hero-logo" 
              />
            </a>

            {/* Right Top Buttons */}
            <div className="d-flex align-items-center gap-3">
              {/* Phone Button */}
              {phone && (
                <a 
                  href={`tel:${phone.replace(/[^\d+]/g, '')}`} 
                  className="btn ttc-hero-top-phone d-lg-inline-flex d-none align-items-center gap-2"
                >
                  <span>{phone}</span>
                  <span className="ttc-hero-arrow-circle">↗</span>
                </a>
              )}

              {/* Explore Membership Button -> Refers to Membership-section */}
              <a 
                href={`#${membershipSectionId}`}
                onClick={(e) => scrollToSection(e, membershipSectionId)}
                className="btn ttc-hero-top-btn d-lg-inline-flex d-none align-items-center gap-2"
              >
                <span>Explore Membership</span>
                <span className="ttc-hero-arrow-circle white">↗</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* MAIN HERO CONTENT */}
      <div className="container position-relative ttc-hero-content py-5 my-auto">
        <div className="row align-items-center gy-5">
          
          {/* Left Column: Text & Primary Actions */}
          <div className="col-12 col-lg-6">
            {/* Tagline */}
            {tagline && <p className="ttc-hero-tagline text-uppercase mb-3">{tagline}</p>}

            {/* Main Heading */}
            <h1 className="ttc-hero-heading text-white mb-4">
              {headingLine1} <br />
              {headingLine2} <br />
              {headingItalic && <span className="ttc-hero-italic">{headingItalic} </span>}
              {headingLine3}
            </h1>

            {/* Paragraph Subtext */}
            {subtext && <p className="ttc-hero-subtext mb-4">{subtext}</p>}

            {/* Button Row */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-5">
              <button
                onClick={()=>{navigate("/book-a-consultation/membership")}} 
                
                className="btn ttc-hero-btn-primary d-inline-flex align-items-center gap-2"
              >
                <span>Get started</span>
                <span className="ttc-hero-btn-icon">↗</span>
              </button>

              {phone && (
                <a 
                  href={`tel:${phone.replace(/[^\d+]/g, '')}`} 
                  className="btn ttc-hero-btn-phone d-inline-flex align-items-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>Member line {phone}</span>
                </a>
              )}
            </div>

            {/* Bottom Doctor Badge -> Refers to Team-led-care section */}
            <a 
              href={`#${teamLedSectionId}`} 
              onClick={(e) => scrollToSection(e, teamLedSectionId)}
              className="ttc-hero-doctor-badge d-inline-flex align-items-center gap-3 text-decoration-none"
            >
              <div className="ttc-hero-avatar-circle">FV</div>
              <div className="ttc-hero-doctor-info">
                <div className="ttc-hero-doctor-name">Dr. Fabio Varlese</div>
                <div className="ttc-hero-doctor-role">Founder • MD, MSc, FRCPC</div>
              </div>
            </a>
          </div>

          {/* Right Column: Doctor Card & Glassmorphism Overlay */}
          <div className="col-12 col-lg-6 position-relative d-flex justify-content-center justify-content-lg-end">
            <div className="ttc-hero-image-stack position-relative">
              
              {/* Doctor Main Card Image */}
              <div className="ttc-hero-img-box">
                <img 
                  src={teamImage} 
                  alt="Dr. Fabio Varlese" 
                  className="img-fluid ttc-hero-main-img"
                />
                {/* Badge on Doctor Photo */}
                <div className="ttc-hero-floating-tag d-flex align-items-center gap-2">
                  <div className="ttc-hero-mini-thumb">
                    <img src={teamImage} alt="" />
                  </div>
                  <div>
                    <div className="fw-bold text-dark" style={{ fontSize: '0.8rem', lineHeight: 1.1 }}>Dr. FABIO VARLESE</div>
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>MD, MSc, FRCPC</div>
                  </div>
                </div>
              </div>

              {/* Glassmorphism Info Overlay */}
              <div className="ttc-hero-glass-card p-4">
                <div className="ttc-hero-glass-tag mb-3">INSIDE THE MEMBERSHIP</div>

                <div className="mb-3">
                  <h6 className="text-white mb-1 fw-semibold">One roof</h6>
                  <p className="ttc-hero-glass-text mb-0">Physicians, naturopaths, physiologists, strategists.</p>
                </div>

                <div className="mb-3">
                  <h6 className="text-white mb-1 fw-semibold">No waitlist</h6>
                  <p className="ttc-hero-glass-text mb-0">Members are not queued behind a public list.</p>
                </div>

                <div className="mb-4">
                  <h6 className="text-white mb-1 fw-semibold">East Tower</h6>
                  <p className="ttc-hero-glass-text mb-0">55 Avenue Road, Suite 2000, Yorkville</p>
                </div>

                {/* Explore Membership Link -> Refers to Membership-section */}
                <a 
                  href={`#${membershipSectionId}`} 
                  onClick={(e) => scrollToSection(e, membershipSectionId)}
                  className="ttc-hero-explore-link d-inline-flex align-items-center gap-2"
                >
                  <span>Explore membership</span>
                  <span>→</span>
                </a>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Bottom spacer for centering balance */}
      <div className="py-2" />
    </section>
  );
}

export default HeroLanding;